import { TaskStatus, RecurrenceInterval } from '../types';
import type { Todo, TodoCreate, TaskHorizon } from '../types';
import { extractDatePart, toEndOfDayDateTime } from './dateUtils';

export interface FormData {
  title: string;
  description: string;
  status: TaskStatus;
  horizon: TaskHorizon | '';
  deadline: string;
  recurrence_interval: RecurrenceInterval;
  tags: string[];
  notes: string;
  next_step: string;
  parent_id: string;
  team_id: string;
  creator_id: string;
  assigned_to_id: string;
  estimated_time_minutes: string;
}

/**
 * Initialize form data from a Todo object or default values
 */
export const initializeFormData = (
  todo?: Todo,
  initialDeadline?: string,
  _initialProjectId?: string,
  initialParentId?: string,
  initialTeamId?: string,
): FormData => {
  return {
    title: todo?.title || '',
    description: todo?.description || '',
    status: todo?.status || TaskStatus.NOT_STARTED,
    horizon: todo?.horizon || '',
    deadline: todo?.deadline ? extractDatePart(todo.deadline) : (initialDeadline || ''),
    recurrence_interval: todo?.recurrence_interval || RecurrenceInterval.NEVER,
    tags: todo?.tags || [],
    notes: todo?.notes || '',
    next_step: todo?.next_step || '',
    parent_id: todo?.parent_id || initialParentId || '',
    team_id: todo?.team_id || initialTeamId || '',
    creator_id: todo?.creator_id || '',
    assigned_to_id: todo?.assigned_to_id || '',
    estimated_time_minutes: todo?.estimated_time_minutes || '',
  };
};

/**
 * Update form data when editing an existing todo
 */
export const updateFormDataFromTodo = (todo: Todo): FormData => {
  return {
    title: todo.title,
    description: todo.description || '',
    status: todo.status,
    horizon: todo.horizon || '',
    deadline: todo.deadline ? extractDatePart(todo.deadline) : '',
    recurrence_interval: todo.recurrence_interval,
    tags: todo.tags,
    notes: todo.notes || '',
    next_step: todo.next_step || '',
    parent_id: todo.parent_id || '',
    team_id: todo.team_id || '',
    creator_id: todo.creator_id || '',
    assigned_to_id: todo.assigned_to_id || '',
    estimated_time_minutes: todo.estimated_time_minutes || '',
  };
};

/**
 * Convert form data to TodoCreate format for API submission
 */
export const prepareSubmitData = (formData: FormData): TodoCreate => {
  // Convert date to proper datetime format if provided, null if empty
  let deadlineDatetime: string | null = null;
  if (formData.deadline) {
    deadlineDatetime = toEndOfDayDateTime(formData.deadline);
  }

  return {
    title: formData.title.trim(),
    description: formData.description?.trim() || undefined,
    deadline: deadlineDatetime,
    status: formData.status,
    horizon: formData.horizon || null,
    recurrence_interval: formData.recurrence_interval,
    tags: formData.tags || [],
    next_step: formData.next_step?.trim() || undefined,
    parent_id: formData.parent_id?.trim() || null,
    team_id: formData.team_id?.trim() || undefined,
    notes: formData.notes?.trim() || undefined,
    source: undefined,
    creator_id: formData.creator_id?.trim() || undefined,
    assigned_to_id: formData.assigned_to_id?.trim() || undefined,
    estimated_time_minutes: formData.estimated_time_minutes?.trim() || undefined,
  };
};

/**
 * Validate form data and return errors
 */
export const validateFormData = (formData: FormData): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Check if title exists and is not just whitespace
  if (!formData.title || !formData.title.trim()) {
    errors.title = 'Title is required';
  } else if (formData.title.trim().length > 200) {
    errors.title = 'Title must be less than 200 characters';
  }

  if (formData.description && formData.description.length > 2000) {
    errors.description = 'Description must be less than 2000 characters';
  }

  return errors;
};

/**
 * Parse tags from comma-separated string
 */
export const parseTagsFromInput = (tagInput: string): string[] => {
  return tagInput
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);
};

/**
 * Format tags array to comma-separated string
 */
export const formatTagsToInput = (tags: string[]): string => {
  return tags.join(', ');
};
